import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useRealtime } from '../hooks/useRealtime'
import { useToast } from '../components/common/ToastProvider'
import ResultsTable from '../components/Results/ResultsTable'
import TrainingTable from '../components/Results/TrainingTable'
import QualiTable from '../components/Results/QualiTable'
import TrialTable from '../components/Results/TrialTable'
import TeamCompetitionTable from '../components/Results/TeamCompetitionTable'
import Startlist from '../components/Results/Startlist'
import RaceFacts from '../components/Results/RaceFacts'
import Flag from '../components/common/Flag'
import axios from 'axios'

export default function ResultsPage() {
  const { fiscodex } = useParams()
  const { setShowTickerExtras, setCompetition, setMessage } = useOutletContext()
  const { addToast } = useToast()
  
  const [activeTab, setActiveTab] = useState('results')
  const [competition, setLocalCompetition] = useState(null)
  const [racefactsLoaded, setRacefactsLoaded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandAllTeams, setExpandAllTeams] = useState(true)
  
  // Refs to track previous values for notifications
  const prevCurrentRef = useRef(null)
  const prevStatusRef = useRef(null)
  const hillRecordRef = useRef(null)
  
  const [state, setState] = useState({
    results: null,
    status: null,
    currentStatus: null,
    current: null,
    next: null,
    light: null,
    raceinfo: null,
    messages: [],
    startlist: null,
    management: null,
    states: [],
    template: '/templates/results/empty.html',
    update: true
  })

  // Handle Faye messages
  const handleFayeMessage = useCallback((data) => {
    setState((prev) => {
      const updates = {}

      if (data.raceinfo) {
        updates.raceinfo = data.raceinfo
        
        // Initialize hill record on first load
        if (!hillRecordRef.current && data.raceinfo.hill_record) {
          hillRecordRef.current = parseFloat(data.raceinfo.hill_record)
        }
      }

      if (data.startlist) {
        updates.startlist = data.startlist
        updates.current = null
        updates.next = null
      }

      if (data.results && prev.update) {
        updates.results = data.results
      }

      if (data.status && prev.update) {
        const newStatus = setStatus(data.status)
        updates.status = newStatus
        updates.currentStatus = newStatus
        
        if (!prev.states.includes(newStatus)) {
          updates.states = [...prev.states, newStatus]
        }
      }

      if (data.current) {
        updates.current = data.current
        
        // Show notification when current jumper changes
        if (prev.current && prevCurrentRef.current !== data.current.bib) {
          const jumperName = `${data.current.firstname} ${data.current.lastname}`
          // addToast(`Now jumping: ${jumperName} (${data.current.nat})`, 'info', 4000)
          
          // Check for hill record
          const distance = data.current.length?.length
          if (distance && hillRecordRef.current && distance > hillRecordRef.current) {
            addToast(`🎉 NEW HILL RECORD! ${distance}m by ${jumperName}!`, 'record', 6000)
            hillRecordRef.current = distance
          }
        }
        prevCurrentRef.current = data.current.bib
      }

      if (data.next) {
        updates.next = data.next
      }

      if (data.light) {
        updates.light = data.light
        
        // Notify on light changes
        // if (prev.light !== data.light) {
        //   if (data.light === 'green') {
        //     addToast('Hill is clear - Ready to jump', 'success', 2000)
        //   } else if (data.light === 'red') {
        //     addToast('Hill closed - Please wait', 'error', 3000)
        //   } else if (data.light === 'yellow') {
        //     addToast('Caution - Checking conditions', 'warning', 2500)
        //   }
        // }
      }

      if (data.messages) {
        updates.messages = data.messages
        
        // Show important messages as toasts
        if (data.messages.length > 0 && data.messages[0].text) {
          const latestMessage = data.messages[0].text
          if (prev.messages.length === 0 || prev.messages[0].text !== latestMessage) {
            addToast(latestMessage, 'info', 5000)
          }
        }
      }
      
      // Notify on status changes (round changes)
      if (data.status && prev.update) {
        const newStatus = setStatus(data.status)
        if (prevStatusRef.current && prevStatusRef.current !== newStatus) {
          const statusNames = {
            't': 'Training',
            'q': 'Qualification',
            'trial': 'Trial Round',
            'r1': 'Round 1',
            'r2': 'Round 2',
            'r3': 'Round 3',
            'r4': 'Round 4'
          }
          addToast(`${statusNames[newStatus] || newStatus} has started!`, 'success', 4000)
        }
        prevStatusRef.current = newStatus
      }

      return { ...prev, ...updates }
    })
  }, [addToast])

  // Subscribe to realtime backend (ActionCable/AnyCable or Faye — toggle via REALTIME_BACKEND env)
  const { connected } = useRealtime(
    fiscodex || null,
    handleFayeMessage
  )

  // Fetch initial data
  useEffect(() => {
    if (!fiscodex) return

    const fetchInitialData = async () => {
      try {
        const response = await axios.get(`/events/${fiscodex}.json`)
        const competitionData = response.data.event
        const data = response.data.data

        setCompetition(competitionData)
        setLocalCompetition(competitionData)
        setShowTickerExtras(true)

        setState({
          results: data.results,
          status: setStatus(data.status),
          currentStatus: setStatus(data.status),
          current: data.current,
          next: data.next,
          light: data.light,
          raceinfo: data.raceinfo,
          messages: data.messages,
          startlist: data.startlist,
          management: data.management,
          states: data.available_states || [],
          update: true
        })

        // Set first message (fall back to a generic label so a stale message
        // from a previously viewed event doesn't linger)
        setMessage(data.messages?.length > 0 ? data.messages[0].text : 'Live Results')
      } catch (error) {
        console.error('Error fetching initial data:', error)
      }
    }

    fetchInitialData()
  }, [fiscodex, setCompetition, setShowTickerExtras, setMessage])

  // Update message when messages change
  useEffect(() => {
    if (state.messages && state.messages.length > 0) {
      setMessage(state.messages[0].text)
    }
  }, [state.messages, setMessage])

  // Fetch race facts data when Race Facts tab is clicked
  useEffect(() => {
    if (activeTab === 'racefacts' && !racefactsLoaded && competition?.id) {
      const fetchRaceFacts = async () => {
        try {
          const racefactsResponse = await axios.get(`/events/${competition.id}/racefacts.json`)
          const racefactsData = racefactsResponse.data
          
          // The API returns competition_management, judges, and jury at the root level
          // We need to wrap them in a management object
          const managementData = {
            competition_management: racefactsData.competition_management,
            judges: racefactsData.judges,
            jury: racefactsData.jury
          }
          
          setState(prev => ({
            ...prev,
            raceinfo: racefactsData.raceinfo || prev.raceinfo,
            management: managementData
          }))
          
          setRacefactsLoaded(true)
        } catch (error) {
          console.error('Error fetching race facts:', error)
        }
      }

      fetchRaceFacts()
    }
  }, [activeTab, racefactsLoaded, competition])

  const setStatus = (status) => {
    if (['t1', 't2', 't3', 't4'].includes(status)) {
      return 't'
    }
    return status
  }

  // Load results for a specific round
  const loadResults = async (round) => {
    try {
      const response = await axios.get(`/events/${fiscodex}/results.json?round=${round}`)
      setState(prev => ({
        ...prev,
        results: response.data,
        status: round,
        update: false // Disable live updates when viewing historical data
      }))
      setActiveTab('results')
    } catch (error) {
      console.error('Error loading results for round:', round, error)
    }
  }

  // Enable live updates (return to current status)
  const enableLiveUpdates = async () => {
    try {
      const response = await axios.get(`/events/${fiscodex}/results.json?round=${state.currentStatus}`)
      setState(prev => ({
        ...prev,
        results: response.data,
        status: prev.currentStatus,
        update: true
      }))
    } catch (error) {
      console.error('Error returning to live results:', error)
      // Fallback: just enable updates without fetching
      setState(prev => ({
        ...prev,
        status: prev.currentStatus,
        update: true
      }))
    }
  }

  // Filter results based on search query
  const filterResults = (results) => {
    if (!searchQuery.trim()) return results
    
    const query = searchQuery.toLowerCase()
    return results.filter(row => 
      row.name?.toLowerCase().includes(query) || 
      row.nation?.toLowerCase().includes(query)
    )
  }

  // Get the appropriate table component based on status
  const getResultsTable = () => {
    if (!state.results) return null

    const filteredResults = filterResults(state.results)

    if (searchQuery.trim() && state.results.length > 0 && filteredResults.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No results match &quot;{searchQuery}&quot;
        </div>
      )
    }

    const props = {
      results: filteredResults,
      status: state.status,
      raceinfo: state.raceinfo,
      current: state.current
    }

    // Check if it's a team competition
    const isTeamCompetition = state.raceinfo?.team === 1

    if (isTeamCompetition && ['r1', 'r2', 'r3', 'r4'].includes(state.status)) {
      return <TeamCompetitionTable {...props} expandAll={expandAllTeams} onToggleAll={() => setExpandAllTeams(!expandAllTeams)} />
    }

    switch (state.status) {
      case 't':
        return <TrainingTable {...props} />
      case 'q':
        return <QualiTable {...props} />
      case 'trial':
        return <TrialTable {...props} />
      case 'r1':
      case 'r2':
      case 'r3':
      case 'r4':
        return <ResultsTable {...props} />
      default:
        return <p>No results template for status: {state.status}</p>
    }
  }

  if (!state.results) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading results...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Sidebar - Current Jumper (Top on Mobile, Right on Desktop) */}
      <div className="order-1 lg:order-2 lg:w-80 lg:flex-shrink-0">
        <div className="lg:sticky lg:top-6 space-y-4">
          {/* Connection Status - Mobile Only */}
          <div className={`lg:hidden connection-status ${connected ? 'connected' : 'disconnected'}`}>
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></span>
            <span>{connected ? 'Connected' : 'Disconnected'}</span>
          </div>

          {/* Hill Status Indicator + Next Jumper */}
          {state.light && (
            <div className={`rounded-lg border overflow-hidden ${
              state.light === 'green' ? 'border-green-200' :
              state.light === 'yellow' ? 'border-yellow-200' :
              state.light === 'red' ? 'border-red-200' :
              'border-gray-200'
            }`}>
              <div
                className={`connection-status flex w-full rounded-none border-0 py-2 ${
                  state.light === 'green' ? 'bg-green-100 text-green-800' :
                  state.light === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                  state.light === 'red' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-600'
                }`}
                role="status"
                aria-live="polite"
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  state.light === 'green' ? 'bg-green-500 animate-pulse' :
                  state.light === 'yellow' ? 'bg-yellow-400 animate-pulse' :
                  state.light === 'red' ? 'bg-red-500' :
                  'bg-gray-400'
                }`}></span>
                <span>
                  {state.light === 'green' ?
                    (state.current ? `${state.current.firstname} ${state.current.lastname} started` : 'Hill Open') :
                   state.light === 'yellow' ?
                    (state.next ? `Get Ready: ${state.next.firstname} ${state.next.lastname}` : 'Get Ready') :
                   state.light === 'red' ? 'Start Gate Closed' :
                   'Status Unknown'}
                </span>
              </div>

              {state.next && (
                <div className="bg-gray-50 p-3">
                  <h4 className="text-xs font-semibold text-gray-500 mb-2">Next Jumper</h4>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-gray-300 text-gray-800 text-xs font-bold rounded">
                      BIB {state.next.bib}
                    </span>
                    <div>
                      <div className="text-sm lg:text-base font-semibold text-gray-900">
                        {state.next.firstname} {state.next.lastname}
                      </div>
                      <div className="text-xs text-gray-600">
                        {state.next.nat?.toUpperCase()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Current Jumper Card */}
          {state.current && (
            <div className="jumper-card">
              <div className="flex items-start gap-4 mb-4">
                <img 
                  src={state.current.image3 || '/images/hill.png'} 
                  alt={`${state.current.firstname} ${state.current.lastname}`}
                  className="w-20 h-20 lg:w-24 lg:h-24 object-cover rounded-lg shadow-md"
                  onError={(e) => { e.target.src = '/images/hill.png' }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-gray-800 text-white text-xs font-bold rounded">
                      BIB {state.current.bib}
                    </span>
                  </div>
                  <h3 className="text-sm lg:text-base font-bold text-gray-900 leading-tight">
                    {state.current.firstname} {state.current.lastname}
                  </h3>
                  <div className="mt-2 text-xs lg:text-sm text-gray-600 space-y-1">
                    <div className="flex items-center gap-2">
                      <span>Nation:</span>
                      <Flag code={state.current.nat} className="text-base" />
                      <span className="font-semibold">{state.current.nat?.toUpperCase()}</span>
                    </div>
                    <div>Age: <span className="font-semibold">{calculateAge(state.current.date_of_birth)}</span></div>
                  </div>
                </div>
              </div>

              {/* Jump Stats */}
              <div className="space-y-3 border-t border-gray-200 pt-4">
                {/* Gate */}
                <div className="flex items-center justify-between text-sm py-1">
                  <span className="text-gray-600">Gate:</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold ${
                      state.current.gatecomp > 0 ? 'text-green-600' : 
                      state.current.gatecomp < 0 ? 'text-red-600' : 
                      'text-gray-700'
                    }`}>
                      {state.current.gatesnumber || '-'}
                    </span>
                    <span className={`font-bold ${
                      state.current.gatecomp > 0 ? 'text-green-600' : 
                      state.current.gatecomp < 0 ? 'text-red-600' : 
                      'text-gray-700'
                    }`}>
                      {state.current.gatecomp 
                        ? `${state.current.gatecomp > 0 ? '+' : ''}${state.current.gatecomp} pts` 
                        : '0 pts'}
                    </span>
                  </div>
                </div>

                {/* Speed */}
                <div className="flex items-center justify-between text-sm py-1">
                  <span className="text-gray-600">Speed:</span>
                  <span className="font-bold text-gray-900">
                    {state.current.speed?.speed ? `${state.current.speed.speed} km/h` : '-'}
                  </span>
                </div>

                {/* Distance */}
                <div className="flex items-center justify-between text-sm py-1">
                  <span className="text-gray-600">Distance:</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold ${state.current.length?.length ? 'text-green-600' : 'text-gray-900'}`}>
                      {state.current.length?.length ? `${state.current.length.length}m` : '-'}
                    </span>
                    <span className={`font-bold ${state.current.length?.points ? 'text-green-600' : 'text-gray-900'}`}>
                      {state.current.length?.points ? state.current.length.points.toFixed(1) : '-'}
                    </span>
                  </div>
                </div>

                {/* Wind - hidden on mobile */}
                <div className="hidden lg:flex items-center justify-between text-sm py-1">
                  <span className="text-gray-600">Wind:</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-bold ${
                      state.current.wind?.wind 
                        ? (state.current.wind.wind < 0 ? 'text-red-600' : 'text-green-600')
                        : 'text-gray-900'
                    }`}>
                      {state.current.wind?.wind && state.current.length?.length 
                        ? `${state.current.wind.wind} m/s` 
                        : '-'}
                    </span>
                    <span className={`font-bold ${
                      state.current.wind?.compensation
                        ? (state.current.wind.compensation < 0 ? 'text-red-600' : 'text-green-600')
                        : 'text-gray-900'
                    }`}>
                      {state.current.wind?.compensation && state.current.length?.length
                        ? `${state.current.wind.compensation > 0 ? '+' : ''}${state.current.wind.compensation.toFixed(1)}`
                        : '-'}
                    </span>
                  </div>
                </div>

                {/* Distance Score - hidden on mobile */}
                <div className="hidden lg:flex items-center justify-between text-sm py-1">
                  <span className="text-gray-600">Distance score:</span>
                  <span className="font-bold text-gray-900">
                    {state.current.length?.points && state.current.length?.length
                      ? (
                        (state.current.length.points || 0) + 
                        (state.current.wind?.compensation || 0) + 
                        (state.current.gatecomp || 0)
                      ).toFixed(1)
                      : '-'}
                  </span>
                </div>

                {/* Judge Scores - hidden on mobile */}
                {state.raceinfo?.judges && (
                  <div className="hidden lg:block border-t border-gray-200 pt-3 mt-3">
                    <div className="grid grid-cols-5 gap-2">
                      {Object.entries(state.raceinfo.judges).slice(0, 5).map(([index, judge]) => {
                        const judgeScore = state.current.judge?.[index];
                        const hasScore = judgeScore && judgeScore.rate !== undefined;
                        
                        return (
                          <div 
                            key={index}
                            className={`text-center p-2 rounded ${
                              hasScore && judgeScore.discard
                                ? 'bg-red-100 opacity-50' 
                                : hasScore
                                ? 'bg-gray-100'
                                : 'bg-gray-50 border border-dashed border-gray-300'
                            }`}
                          >
                            <div className={`font-bold text-sm ${hasScore ? 'text-gray-900' : 'text-gray-400'}`}>
                              {hasScore ? judgeScore.rate.toFixed(1) : '-'}
                            </div>
                            <Flag code={judge.nation} className="mx-auto mt-1 text-lg" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Judge Score - hidden on mobile */}
                <div className="hidden lg:flex items-center justify-between text-sm border-t border-gray-200 pt-3 mt-1 py-1">
                  <span className="text-gray-600">Judge score:</span>
                  <span className={`font-bold ${
                    state.current.judgetotal?.points ? 'text-green-600' : 'text-gray-900'
                  }`}>
                    {state.current.judgetotal?.points 
                      ? state.current.judgetotal.points.toFixed(1) 
                      : '-'}
                  </span>
                </div>

                {/* Total Score */}
                <div className="flex items-center justify-between text-base font-semibold border-t border-gray-200 pt-3 mt-1 py-1">
                  <span className="text-gray-700">Total score:</span>
                  <span className="text-gray-900 text-lg">
                    {state.current.points?.points 
                      ? state.current.points.points.toFixed(1) 
                      : '-'}
                  </span>
                </div>

                {/* Rank */}
                <div className="flex items-center justify-between text-base font-semibold py-1">
                  <span className="text-gray-700">Rank:</span>
                  <span className="text-gray-900 text-lg">
                    {state.current.cumul?.rank || '-'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Results Table (Bottom on Mobile, Left on Desktop) */}
      <div className="order-2 lg:order-1 flex-1">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Header with Tabs */}
          <div className="px-4 lg:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h2 className="text-xl lg:text-2xl font-bold text-gray-900">
                  {state.status === 't' && 'Training'}
                  {state.status === 'q' && 'Qualification'}
                  {state.status === 'trial' && 'Trial Round'}
                  {state.status === 'r1' && 'Round 1'}
                  {state.status === 'r2' && 'Round 2'}
                  {state.status === 'r3' && 'Round 3'}
                  {state.status === 'r4' && 'Round 4'}
                </h2>
                
                {/* Round Selector Dropdown */}
                {state.states && state.states.length > 1 && (
                  <div className="relative">
                    <select
                      value={state.status}
                      onChange={(e) => {
                        if (e.target.value === state.currentStatus) {
                          enableLiveUpdates()
                        } else {
                          loadResults(e.target.value)
                        }
                      }}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      {state.states.map((round) => (
                        <option key={round} value={round}>
                          {round === 't' && 'Training'}
                          {round === 'q' && 'Qualification'}
                          {round === 'trial' && 'Trial Round'}
                          {round === 'r1' && 'Round 1'}
                          {round === 'r2' && 'Round 2'}
                          {round === 'r3' && 'Round 3'}
                          {round === 'r4' && 'Round 4'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Live indicator */}
                {!state.update && state.currentStatus && (
                  <button
                    onClick={enableLiveUpdates}
                    className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                  >
                    Return to Live
                  </button>
                )}
              </div>
              
              {/* Connection Status - Desktop Only */}
              <div className={`hidden lg:flex connection-status ${connected ? 'connected' : 'disconnected'}`}>
                <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}></span>
                <span>{connected ? 'Connected' : 'Disconnected'}</span>
              </div>
            </div>
            
            {/* Search Field - Only visible on Results tab */}
            {activeTab === 'results' && (
              <div className="mt-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or nation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  />
                  <svg 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Tabs Navigation */}
            <div className="flex justify-between items-center border-b border-gray-300 mt-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('results')}
                  className={`px-4 py-2 font-semibold transition-colors ${
                    activeTab === 'results'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Results
                </button>
                <button
                  onClick={() => setActiveTab('startlist')}
                  className={`px-4 py-2 font-semibold transition-colors ${
                    activeTab === 'startlist'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Startlist
                </button>
                <button
                  onClick={() => setActiveTab('racefacts')}
                  className={`px-4 py-2 font-semibold transition-colors ${
                    activeTab === 'racefacts'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Race Facts
                </button>
              </div>
              
              {/* Expand/Collapse Button - Only show for team competitions on Results tab */}
              {activeTab === 'results' && state.raceinfo?.team === 1 && ['r1', 'r2', 'r3', 'r4'].includes(state.status) && (
                <button
                  onClick={() => setExpandAllTeams(!expandAllTeams)}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors flex items-center gap-2 mb-0.5"
                >
                  {expandAllTeams ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      Collapse All
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Expand All
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4 lg:p-6">
            {activeTab === 'results' && (
              <div className="overflow-x-auto">
                {getResultsTable()}
              </div>
            )}
            
            {activeTab === 'startlist' && (
              <Startlist startlist={state.startlist} />
            )}
            
            {activeTab === 'racefacts' && (
              <RaceFacts 
                competition={competition} 
                raceinfo={state.raceinfo}
                management={state.management}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function
function calculateAge(dateString) {
  if (!dateString) return 'n/a'
  
  const today = new Date()
  const birthDate = new Date(dateString)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  
  return age
}
